import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { NumericInput } from "@/components/ui/numeric-input";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import {
  ChevronDown,
  ChevronRight,
  Info,
  Plus,
  Trash2,
} from "lucide-react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import type { FoodNutrientTypeId } from "@brewbase/db/types";

type NutritionalFormat = RouterOutputs["food"]["nutritionalFormats"]["list"][number];

interface FormatNutrientRow {
  id: string;
  nutrientTypeId: string;
  displayName: string;
  conversionFactor: number | null;
  displayUnit: string;
  dailyReference: number | null;
  mandatory: boolean;
}

function toRow(n: NutritionalFormat["nutrients"][number]): FormatNutrientRow {
  return {
    id: String(n.nutrient_type_id) + "-" + n.display_order,
    nutrientTypeId: String(n.nutrient_type_id),
    displayName: n.display_name ?? "",
    conversionFactor: n.conversion_factor,
    displayUnit: n.display_unit ?? "",
    dailyReference: n.daily_reference,
    mandatory: n.mandatory,
  };
}

function useFormatNutrients(format: NutritionalFormat) {
  const utils = trpc.useUtils();
  const [rows, setRows] = useState<FormatNutrientRow[]>(() =>
    format.nutrients.map(toRow),
  );
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const setNutrientsMutation = trpc.food.nutritionalFormats.setNutrients.useMutation({
    onSuccess: () => utils.food.nutritionalFormats.list.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const save = useCallback(
    (currentRows: FormatNutrientRow[]) => {
      setNutrientsMutation.mutate({
        format_id: format.id,
        nutrients: currentRows
          .filter((r) => r.nutrientTypeId)
          .map((r, i) => ({
            nutrient_type_id: parseInt(r.nutrientTypeId) as FoodNutrientTypeId,
            display_name: r.displayName.trim() || null,
            conversion_factor: r.conversionFactor ?? 1.0,
            display_unit: r.displayUnit.trim() || null,
            daily_reference: r.dailyReference,
            display_order: i,
            mandatory: r.mandatory,
          })),
      });
    },
    [format.id, setNutrientsMutation],
  );

  const saveOnBlur = useCallback(() => {
    save(rowsRef.current);
  }, [save]);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      { id: crypto.randomUUID(), nutrientTypeId: "", displayName: "", conversionFactor: 1, displayUnit: "", dailyReference: null, mandatory: false },
    ]);
  }, []);

  const removeRow = useCallback(
    (id: string) => {
      setRows((prev) => {
        const next = prev.filter((r) => r.id !== id);
        save(next);
        return next;
      });
    },
    [save],
  );

  const updateRow = useCallback(
    (id: string, field: keyof Omit<FormatNutrientRow, "id">, value: string | number | null | boolean) => {
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    },
    [],
  );

  const updateRowAndSave = useCallback(
    (id: string, patch: Partial<Omit<FormatNutrientRow, "id">>) => {
      setRows((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
        save(next);
        return next;
      });
    },
    [save],
  );

  return { rows, addRow, removeRow, updateRow, updateRowAndSave, saveOnBlur };
}

function FormatNutrientRowEditor({
  row,
  baseUnit,
  nutrientOptions,
  baseUnitById,
  onUpdate,
  onUpdateAndSave,
  onSaveOnBlur,
  onRemove,
}: {
  row: FormatNutrientRow;
  baseUnit: string;
  nutrientOptions: ComboboxOption[];
  baseUnitById: Map<string, string>;
  onUpdate: (field: keyof Omit<FormatNutrientRow, "id">, value: string | number | null | boolean) => void;
  onUpdateAndSave: (patch: Partial<Omit<FormatNutrientRow, "id">>) => void;
  onSaveOnBlur: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_44px_110px_72px_72px_72px_32px_28px] gap-1.5 items-center">
      <Combobox
        options={nutrientOptions}
        value={row.nutrientTypeId}
        onValueChange={(v) => {
          const unit = baseUnitById.get(v) ?? "";
          onUpdateAndSave({
            nutrientTypeId: v,
            displayUnit: row.displayUnit || unit,
          });
        }}
        placeholder="Select..."
        searchPlaceholder="Search..."
        emptyState="None"
        triggerClassName="h-7 text-xs w-full"
        portal={false}
      />
      <span className="h-7 flex items-center text-xs text-muted-foreground px-1 select-none">
        {baseUnit || "\u2014"}
      </span>
      <Input
        value={row.displayName}
        onChange={(e) => onUpdate("displayName", e.target.value)}
        onBlur={onSaveOnBlur}
        placeholder="e.g. Salt"
        className="h-7 text-xs"
      />
      <NumericInput
        value={row.conversionFactor}
        onValueChange={(v) => onUpdate("conversionFactor", v)}
        onBlur={onSaveOnBlur}
        placeholder="1.0"
        className="h-7 text-xs"
      />
      <Input
        value={row.displayUnit}
        onChange={(e) => onUpdate("displayUnit", e.target.value)}
        onBlur={onSaveOnBlur}
        placeholder={baseUnit || "g"}
        className="h-7 text-xs"
      />
      <NumericInput
        value={row.dailyReference}
        onValueChange={(v) => onUpdate("dailyReference", v)}
        onBlur={onSaveOnBlur}
        placeholder={"\u2014"}
        className="h-7 text-xs"
      />
      <div className="flex items-center justify-center">
        <Checkbox
          checked={row.mandatory}
          onCheckedChange={(checked) =>
            onUpdateAndSave({ mandatory: !!checked })
          }
          className="h-4 w-4"
        />
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  );
}

function ColumnHeaders() {
  return (
    <div className="grid grid-cols-[1fr_44px_110px_72px_72px_72px_32px_28px] gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wide px-0.5">
      <span>Nutrient</span>
      <span>Base</span>
      <span>Label Name</span>
      <span className="flex items-center gap-1">
        Factor
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3 w-3 cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px] text-xs normal-case tracking-normal font-normal">
            Multiply the base value by this factor to get the label value.
            E.g. Sodium (mg) to Salt (g): factor = 0.00254
          </TooltipContent>
        </Tooltip>
      </span>
      <span>Label Unit</span>
      <span className="flex items-center gap-1">
        Daily Ref
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3 w-3 cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px] text-xs normal-case tracking-normal font-normal">
            Daily reference intake for %DV calculation. Leave blank to hide %DV for this nutrient.
          </TooltipContent>
        </Tooltip>
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-help">Req</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-xs normal-case tracking-normal font-normal">
          Required (mandatory) on the label. Always shown even if the value is zero.
        </TooltipContent>
      </Tooltip>
      <span />
    </div>
  );
}

export function FormatCard({
  format,
  nutrientTypes,
  onDelete,
  isDeleting,
}: {
  format: NutritionalFormat;
  nutrientTypes: RouterOutputs["food"]["nutrientTypes"]["list"];
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const { rows, addRow, removeRow, updateRow, updateRowAndSave, saveOnBlur } =
    useFormatNutrients(format);

  const nutrientOptions = useMemo<ComboboxOption[]>(
    () => nutrientTypes.map((t) => ({ value: String(t.id), label: t.name })),
    [nutrientTypes],
  );

  const baseUnitById = useMemo(
    () => new Map(nutrientTypes.map((t) => [String(t.id), t.default_unit ?? ""])),
    [nutrientTypes],
  );

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-muted/40">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left cursor-pointer"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <span className="text-sm font-medium truncate">{format.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            ({format.nutrients.length} nutrients)
          </span>
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive shrink-0"
          disabled={isDeleting}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {expanded && (
        <div className="border-t px-3 py-3 space-y-2">
          {rows.length > 0 && <ColumnHeaders />}

          {rows.map((row) => (
            <FormatNutrientRowEditor
              key={row.id}
              row={row}
              baseUnit={baseUnitById.get(row.nutrientTypeId) ?? ""}
              nutrientOptions={nutrientOptions}
              baseUnitById={baseUnitById}
              onUpdate={(field, value) => updateRow(row.id, field, value)}
              onUpdateAndSave={(patch) => updateRowAndSave(row.id, patch)}
              onSaveOnBlur={saveOnBlur}
              onRemove={() => removeRow(row.id)}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-6 text-[10px]"
            onClick={addRow}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Nutrient
          </Button>
        </div>
      )}
    </div>
  );
}
